import { inject } from 'mobx-react';
import * as React from 'react';

import { ContextStore, RouteStore } from 'bernie-core';

import { adaptSearchCriteria } from 'src/common/stores/adapters/bex-api/common/search-criteria-adapter';
import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { GalleryPlaceholderData, SummaryPlaceholderData } from 'src/common/stores/contexts';
import { getDateRangeInput } from 'src/common/stores/adapters';
import { getShoppingContextInput } from 'src/common/stores/adapters/bex-api/search/shopping-context-adapter';
import { MultiItemStore } from 'src/common/stores/multi-item-store';

import { DamageLiability, DateInput, PropertyDateRangeInput, PropertySearchCriteriaInput } from 'src/common/__generated__/api';
import { ShoppingContextInput } from '__generated__/api';

interface SharedUiPropertyDetailsComponentProps {
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  context?: ContextStore;
}

interface SharedUiPropertyDetailsWithInputsProps {
  hotelId: string;
  dateRange: PropertyDateRangeInput;
  shoppingContext: ShoppingContextInput | undefined;
  searchCriteria: PropertySearchCriteriaInput;
  damageLiability: DamageLiability;
}

export interface SharedUiWrapperProps {
  inputs?: {
    propertyId: string;
    isDateless: boolean;
    dateRange: PropertyDateRangeInput;
    shoppingContext: ShoppingContextInput;
    searchCriteria: PropertySearchCriteriaInput;
    damageLiability: DamageLiability;
  };
  placeholderData?: GalleryPlaceholderData | SummaryPlaceholderData;
  className?: string;
}

/**
 * HOC that'll check if the hotelId is properly set, and return a memoized component that'll
 * only re-trigger its graphql query if the hotelId changes. It will inject the inputs props
 * so your wrapper can pass it down to its shared-ui component, all other props are simply drilled
 * down.
 * @param SharedUiWrapper {React.FC<SharedUiWrapperProps>}
 */
export const withSharedUiPropertyDetails = <F extends any>(SharedUiWrapper: React.FC<SharedUiWrapperProps & F>) => {
  const SharedUiPropertyDetailsComponent: React.FC<SharedUiPropertyDetailsComponentProps & F> = inject(
    'currentHotel',
    'multiItem',
    'context',
    'routeStore'
    // @ts-ignore
  )(({ currentHotel, multiItem, context, routeStore, ...props }) => {
    const { hotelId, searchCriteria } = currentHotel || {};
    const isDateless = searchCriteria?.isDateless;

    const damageLiability: DamageLiability = adaptDamageLiability(routeStore);

    const propertySearchCriteria = adaptSearchCriteria(context, searchCriteria, false, null);

    const dateRange = getDateRangeInput(searchCriteria);
    const dateRangeHash = dateRange ? hashDateRange(dateRange) : '';

    const multiItemContext = multiItem?.multiItemContext;
    const shoppingContext = getShoppingContextInput(multiItemContext);

    // This is safe, because you're not actually rendering the component here
    // So we're golden even if the hotelId is null.
    const Component = React.useMemo(() => withSharedUiPropertyDetailsInputs(SharedUiWrapper, isDateless), [
      hotelId,
      dateRangeHash,
      isDateless,
      shoppingContext.multiItem?.id,
      shoppingContext.multiItem?.packageType,
      damageLiability,
    ]);

    if (!hotelId) {
      return null;
    }

    return (
      <Component
        hotelId={String(hotelId)}
        dateRange={dateRange}
        shoppingContext={shoppingContext}
        searchCriteria={propertySearchCriteria}
        damageLiability={damageLiability}
        {...props}
      />
    );
  });

  return SharedUiPropertyDetailsComponent;
};

/**
 * Function that builds the graphql input for property-details shared-ui
 * components. Very useful to make sure that all shared-ui components get
 * the same inputs, so queries aren't duplicated.
 * Not for external use, only use within {withSharedUiPropertyDetails}
 * or at your own risk.
 */
const withSharedUiPropertyDetailsInputs = (SharedUiWrapper: React.FC<SharedUiWrapperProps>, isDateless: boolean) => {
  const SharedUiPropertyDetailsWithInputs: React.FC<SharedUiPropertyDetailsWithInputsProps> = ({
    hotelId,
    dateRange,
    shoppingContext,
    searchCriteria,
    damageLiability,
    ...props
  }) => {
    const dateRangeHash = dateRange ? hashDateRange(dateRange) : '';

    const inputs = React.useMemo(
      () => ({
        propertyId: hotelId,
        dateRange,
        isDateless,
        shoppingContext,
        searchCriteria,
        damageLiability,
      }),
      [
        hotelId,
        isDateless,
        dateRangeHash,
        shoppingContext.multiItem?.id,
        shoppingContext.multiItem?.packageType,
        searchCriteria,
        damageLiability,
      ]
    );

    return <SharedUiWrapper inputs={inputs} {...props} />;
  };

  return SharedUiPropertyDetailsWithInputs;
};

export function adaptDamageLiability(routeStore: RouteStore): DamageLiability {
  return routeStore?.queryParams?.damageLiability === 'COF' ? DamageLiability.COF : null;
}

function hashDateInput(date: DateInput): string {
  return `${date.year}-${date.month}-${date.day}`;
}

function hashDateRange(dateRange: PropertyDateRangeInput): string {
  return `${hashDateInput(dateRange.checkInDate)}--${hashDateInput(dateRange.checkOutDate)}`;
}
